// 測定ビュー専用の状態コントローラ（§14）。AppStore とは完全分離。
// - 構造変化（カウント・セッション操作・開始/停止）は subscribe リスナへ
// - 1秒 tick（タイマー表示・サマリのライブ更新）は onTick リスナへ
// と通知チャンネルを分け、タイマーが他ビューの再描画を巻き込まないようにする。
// 全ミューテーションで自動保存。走行中は 5 秒ごとの heartbeat と
// pagehide/visibilitychange(hidden) の即時保存でリロード復元に備える（§14 タイマー会計）。
import { heartbeat, isRunning, pauseTimer, restoreSession, startTimer } from '../model/measure';
import {
  countersFromSet,
  createDropCounter,
  createExtraCost,
  createMeasureSession,
  deleteSession,
  getActiveSessionId,
  loadSessions,
  saveSession,
  setActiveSessionId,
} from '../store/measure-persist';
import type { MeasureDropCounter, MeasureExtraCost, MeasureSession } from '../store/measure-schema';
import type { FarmSet } from '../store/schema';

const HEARTBEAT_MS = 5_000;
const UNDO_LIMIT = 50;

/** 誤操作しやすいカウント系操作のみ逆操作で戻す（スナップショットはタイマーまで巻き戻すので不採用）。 */
type UndoEntry =
  | { kind: 'runs'; prev: number }
  | { kind: 'drop-count'; counterId: string; prev: number }
  | { kind: 'counter-added'; counterId: string }
  | { kind: 'extra-cost-added'; costId: string };

export class MeasureController {
  session: MeasureSession | null = null;
  sessions: MeasureSession[] = [];
  league: string | null = null;

  private readonly listeners = new Set<() => void>();
  private readonly tickListeners = new Set<() => void>();
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private msSinceHeartbeat = 0;
  private undoStack: UndoEntry[] = [];

  constructor() {
    // タブ閉じ・リロード直前に走行状態を保存（通常経路では損失ゼロ、heartbeat はクラッシュ保険）。
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flush());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flush();
      });
    }
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** 1秒 tick（走行中のみ発火）。タイマー表示・実測サマリの部分更新用。 */
  onTick(fn: () => void): () => void {
    this.tickListeners.add(fn);
    return () => this.tickListeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private emitTick(): void {
    for (const fn of this.tickListeners) fn();
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  // ── リーグ・セッション管理 ──────────────────────

  /** リーグ切替（起動時含む）。走行中なら停止・保存してから読み直す。 */
  onLeagueChanged(league: string): void {
    this.pause();
    this.league = league;
    this.sessions = loadSessions(league);
    // 走行中のまま保存されていたセッションは lastTickAt までを畳んで停止状態で復元。
    for (const s of this.sessions) {
      if (s.runningSince != null) {
        restoreSession(s);
        saveSession(s);
      }
    }
    const activeId = getActiveSessionId(league);
    this.session = this.sessions.find((s) => s.id === activeId) ?? this.sessions[0] ?? null;
    if (this.session) setActiveSessionId(league, this.session.id);
    this.undoStack = [];
    this.emit();
  }

  newSession(linkedSet?: FarmSet): void {
    if (!this.league) return;
    this.pause();
    const s = createMeasureSession(this.league, linkedSet);
    this.sessions.push(s);
    this.session = s;
    setActiveSessionId(this.league, s.id);
    saveSession(s);
    this.undoStack = [];
    this.emit();
  }

  switchSession(id: string): void {
    if (!this.league || this.session?.id === id) return;
    this.pause();
    const next = this.sessions.find((s) => s.id === id);
    if (!next) return;
    this.session = next;
    setActiveSessionId(this.league, id);
    this.undoStack = [];
    this.emit();
  }

  deleteCurrentSession(): void {
    if (!this.league || !this.session) return;
    this.stopTimers();
    deleteSession(this.league, this.session.id);
    this.sessions = this.sessions.filter((s) => s.id !== this.session!.id);
    this.session = this.sessions[0] ?? null;
    if (this.session) setActiveSessionId(this.league, this.session.id);
    this.undoStack = [];
    this.emit();
  }

  /** ファームセットへのリンク（変更）。セット側の行でカウンタ未作成のものを追補する。 */
  linkSet(set: FarmSet | null): void {
    const s = this.session;
    if (!s) return;
    s.linkedSetId = set?.id;
    if (set) this.addMissingCounters(set);
    this.save();
    this.emit();
  }

  /** セットの行/メンバーのうちカウンタが無いものを追加（観測窓は現在の runs から）。 */
  addMissingCounters(set: FarmSet): number {
    const s = this.session;
    if (!s) return 0;
    const has = new Set<string>();
    for (const c of s.drops) {
      if (c.source.kind === 'set-line') has.add(`l:${c.source.lineId}`);
      else if (c.source.kind === 'set-member') has.add(`m:${c.source.groupId}:${c.source.memberId}`);
    }
    const missing = countersFromSet(set, s.runs).filter((c) =>
      c.source.kind === 'set-line' ? !has.has(`l:${c.source.lineId}`) : c.source.kind === 'set-member' ? !has.has(`m:${c.source.groupId}:${c.source.memberId}`) : false,
    );
    s.drops.push(...missing);
    if (missing.length) this.save();
    return missing.length;
  }

  // ── タイマー ──────────────────────

  running(): boolean {
    return this.session != null && isRunning(this.session);
  }

  start(): void {
    const s = this.session;
    if (!s || isRunning(s)) return;
    startTimer(s, Date.now());
    this.save();
    this.ensureTimers();
    this.emit();
  }

  pause(): void {
    const s = this.session;
    this.stopTimers();
    if (!s || !isRunning(s)) return;
    pauseTimer(s, Date.now());
    this.save();
    this.emit();
  }

  toggle(): void {
    if (this.running()) this.pause();
    else this.start();
  }

  private ensureTimers(): void {
    if (this.tickTimer != null) return;
    this.msSinceHeartbeat = 0;
    this.tickTimer = setInterval(() => {
      const s = this.session;
      if (!s || !isRunning(s)) {
        this.stopTimers();
        return;
      }
      this.msSinceHeartbeat += 1000;
      if (this.msSinceHeartbeat >= HEARTBEAT_MS) {
        this.msSinceHeartbeat = 0;
        heartbeat(s, Date.now());
        saveSession(s);
      }
      this.emitTick();
    }, 1000);
  }

  private stopTimers(): void {
    if (this.tickTimer != null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  /** 走行中の即時保存（pagehide / visibilitychange(hidden)）。 */
  private flush(): void {
    const s = this.session;
    if (!s || !isRunning(s)) return;
    heartbeat(s, Date.now());
    saveSession(s);
  }

  // ── カウント（Undo 対象） ──────────────────────

  addRun(delta: 1 | -1 = 1): void {
    const s = this.session;
    if (!s) return;
    this.pushUndo({ kind: 'runs', prev: s.runs });
    s.runs = Math.max(0, s.runs + delta);
    this.save();
    this.emit();
  }

  addDrop(counterId: string, delta: 1 | -1 = 1): void {
    const s = this.session;
    const c = s?.drops.find((x) => x.id === counterId);
    if (!s || !c) return;
    this.pushUndo({ kind: 'drop-count', counterId, prev: c.count });
    c.count = Math.max(0, c.count + delta);
    this.save();
    this.emit();
  }

  /** ドロップカウンタ追加（観測窓は現在の runs から）。 */
  addCounter(unit: string): MeasureDropCounter | null {
    const s = this.session;
    if (!s) return null;
    const c = createDropCounter(unit, s.runs);
    s.drops.push(c);
    this.pushUndo({ kind: 'counter-added', counterId: c.id });
    this.save();
    this.emit();
    return c;
  }

  removeCounter(counterId: string): void {
    const s = this.session;
    if (!s) return;
    s.drops = s.drops.filter((c) => c.id !== counterId);
    this.undoStack = this.undoStack.filter(
      (u) => !((u.kind === 'drop-count' || u.kind === 'counter-added') && u.counterId === counterId),
    );
    this.save();
    this.emit();
  }

  addExtraCostRow(unit: string): MeasureExtraCost | null {
    const s = this.session;
    if (!s) return null;
    const c = createExtraCost(unit);
    s.extraCosts.push(c);
    this.pushUndo({ kind: 'extra-cost-added', costId: c.id });
    this.save();
    this.emit();
    return c;
  }

  removeExtraCost(costId: string): void {
    const s = this.session;
    if (!s) return;
    s.extraCosts = s.extraCosts.filter((c) => c.id !== costId);
    this.undoStack = this.undoStack.filter((u) => !(u.kind === 'extra-cost-added' && u.costId === costId));
    this.save();
    this.emit();
  }

  undo(): void {
    const s = this.session;
    const entry = this.undoStack.pop();
    if (!s || !entry) return;
    switch (entry.kind) {
      case 'runs':
        s.runs = entry.prev;
        break;
      case 'drop-count': {
        const c = s.drops.find((x) => x.id === entry.counterId);
        if (c) c.count = entry.prev;
        break;
      }
      case 'counter-added':
        s.drops = s.drops.filter((c) => c.id !== entry.counterId);
        break;
      case 'extra-cost-added':
        s.extraCosts = s.extraCosts.filter((c) => c.id !== entry.costId);
        break;
    }
    this.save();
    this.emit();
  }

  /** 取り込み成功後に呼ぶ（取り込み跨ぎの取り消しはウォーターマークと矛盾するため）。 */
  clearUndo(): void {
    this.undoStack = [];
  }

  private pushUndo(entry: UndoEntry): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > UNDO_LIMIT) this.undoStack.shift();
  }

  // ── 汎用ミューテーション ──────────────────────

  /** 名前・価値などのフィールド更新（Undo 対象外）。 */
  update(fn: (s: MeasureSession) => void): void {
    const s = this.session;
    if (!s) return;
    fn(s);
    this.save();
    this.emit();
  }

  /** 入力中のライブ更新用: 保存はするが emit しない（フォーカス維持は呼び出し側の責務）。 */
  updateQuiet(fn: (s: MeasureSession) => void): void {
    const s = this.session;
    if (!s) return;
    fn(s);
    this.save();
  }

  private save(): void {
    if (this.session) saveSession(this.session);
  }
}
